import React, { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Stack, Typography, Box, Link, CircularProgress, useTheme } from '@mui/material'
import { workosAuthService } from 'services/workos-auth.service'
import { ROUTES } from 'routes'
import { AuthLogo } from './components/AuthLogo'
import { authGlassCardSx } from './components/authGlassCard'
import { AUTH_TITLE_SX } from './components/authType'

/**
 * VERA-1095: unauthenticated landing page for the signup email-verification
 * link — `/verify-email?token=...`. Auto-submits the token on mount (no form
 * to fill in, unlike PasswordSetup's reset mode) and reports success,
 * invalid-link, or generic-error states.
 *
 * A missing token is distinguished from a rejected one so a stripped/mangled
 * link reads as "this link is invalid" rather than a confusing generic
 * failure, mirroring PasswordSetup's isInvalidResetLink handling.
 */
export const VerifyEmailPage = () => {
  const theme = useTheme()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')

  const [status, setStatus] = useState<'verifying' | 'success' | 'invalid' | 'error'>(
    token ? 'verifying' : 'invalid',
  )
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!token) return

    let cancelled = false
    ;(async () => {
      const result = await workosAuthService.confirmEmailVerification(token)
      if (cancelled) return
      if (result.success) {
        setStatus('success')
      } else {
        setStatus('error')
        setError(result.error || 'This verification link is invalid or has expired.')
      }
    })()

    return () => {
      cancelled = true
    }
  }, [token])

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        // VERA-1095: backdrop hoisted to LayoutAuthAmbient, which owns it
        // theme-aware for every auth route. This shell is layout only.
        bgcolor: 'transparent',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        // Phone gutter, as PasswordSetup and the callback shell (the column ran flush
        // to both screen edges at 390px); it is also the phone sheet's side inset.
        px: 3,
      }}
    >
      {/* VERA-1095: the page-local blob pair was DELETED here. These pages are
          routed under `LayoutAuthAmbient`, which renders the shared
          `SignInBackground` for every auth screen — so this page was drawing a
          second, STATIC copy of the same two blobs on top of the shared
          animated ones, which is why this backdrop read lighter and more purple
          than sign-in's. The shared one is strictly better: it orbits, it
          breathes, and it survives navigation. */}

      {/* Main content */}
      <Box
        sx={{
          // Phones only: the solid glass sheet every auth page has below `sm`
          // (authGlassCard.ts, 2026-10-08). This page never had a desktop card, and
          // desktop is unchanged. FIRST, so `position`/`zIndex` below win.
          ...authGlassCardSx(theme, { desktop: false }),
          position: 'relative',
          zIndex: 2,
          width: '396px',
          display: 'flex',
          flexDirection: 'column',
          gap: '60px',
          alignItems: 'center',
        }}
      >
        {/* Logo — `AuthLogo`, not a hand-rolled `<Box component="img">`. This
            page and PasswordSetup each carried their own copy of the same
            140px mark with the same `brightness(0) invert(1)`, which is the
            filter that has to become mode-aware (the source SVG is black). One
            component means that decision is made once instead of three times,
            and both pages pick up the link + keyboard semantics sign-in
            already had. */}
        <AuthLogo />

        <Stack spacing={3} width="100%" alignItems="center">
          {status === 'verifying' && (
            <Stack alignItems="center" spacing={2}>
              {/* Unlike the CTA's spinner, this one sits on the PAGE, not on a
                  purple button — so it follows `text.primary` rather than
                  staying white. */}
              <CircularProgress size={28} thickness={3} sx={{ color: 'text.primary' }} />
              <Typography sx={{ fontSize: '14px', color: 'text.secondary' }}>
                Verifying your email…
              </Typography>
            </Stack>
          )}

          {status === 'success' && (
            <Stack spacing={2} width="100%">
              <Typography sx={AUTH_TITLE_SX}>
                Email verified
              </Typography>
              <Typography sx={{ fontSize: '14px', color: 'text.secondary', lineHeight: 1.5 }}>
                Your email address is verified. You can now sign in with your email and password.
              </Typography>
              <Box sx={{ textAlign: 'center', py: '4px' }}>
                <Link
                  component="button"
                  onClick={() => navigate(ROUTES.SIGN_IN, { replace: true })}
                  sx={{
                    color: 'text.primary',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    fontSize: '14px',
                    lineHeight: 1.4,
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  Continue to sign in
                </Link>
              </Box>
            </Stack>
          )}

          {status === 'invalid' && (
            <Stack spacing={2} width="100%">
              <Typography sx={AUTH_TITLE_SX}>
                This link is invalid
              </Typography>
              <Typography sx={{ fontSize: '14px', color: 'text.secondary', lineHeight: 1.5 }}>
                This verification link is missing or malformed. Sign in and use Resend to get a new one.
              </Typography>
              <Box sx={{ textAlign: 'center', py: '4px' }}>
                <Link
                  component="button"
                  onClick={() => navigate(ROUTES.SIGN_IN, { replace: true })}
                  sx={{
                    color: 'text.primary',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    fontSize: '14px',
                    lineHeight: 1.4,
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  Back to sign in
                </Link>
              </Box>
            </Stack>
          )}

          {status === 'error' && (
            <Stack spacing={2} width="100%">
              <Typography sx={AUTH_TITLE_SX}>
                We couldn't verify your email
              </Typography>
              <Typography sx={{ fontSize: '14px', color: 'text.secondary', lineHeight: 1.5 }}>
                {error}
              </Typography>
              <Box sx={{ textAlign: 'center', py: '4px' }}>
                <Link
                  component="button"
                  onClick={() => navigate(ROUTES.SIGN_IN, { replace: true })}
                  sx={{
                    color: 'text.primary',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    fontSize: '14px',
                    lineHeight: 1.4,
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  Back to sign in
                </Link>
              </Box>
            </Stack>
          )}
        </Stack>
      </Box>
    </Box>
  )
}
